export enum PaStatusBucket {
  // Approved buckets
  APPROVAL_ON_FILE = "approval_on_file",

  // Denied buckets
  DENIAL_ON_FILE = "denial_on_file",
  NOT_COVERED_PLAN_EXCLUSION = "not_covered_plan_exclusion",
  MEDICALLY_UNNECESSARY = "medically_unnecessary",
  STEP_THERAPY_REQUIRED = "step_therapy_required",

  // Onshore Assistance buckets
  DONE_BY_ONSHORE = "done_by_onshore",

  // NA Outcome buckets
  ASKED_CLIENT_ASSISTANCE = "asked_client_assistance",
  EXPIRED = "expired",
  PROCESSOR_ERROR = "processor_error",
  UNABLE_TO_PROCESS_CALL_REQUIRED = "unable_to_process_call_required",
  CANCELLED_CLOSED_DUPLICATE = "cancelled_closed_duplicate",
  ELIGIBILITY_ERROR = "eligibility_error",
  PROGRAM_PREREQUISITE = "program_prerequisite",
  NO_REVIEW_NEEDED = "no_review_needed",
  BENEFIT_LIMITATION = "benefit_limitation",
  UNSPECIFIED_SYSTEM_FAILURE = "unspecified_system_failure",
  OTHERS = "others",

  // No predefined bucket
  NO_PREDEFINED_BUCKETS = "no_predefined_buckets",
}

export const PaStatusBucketLabels: Record<PaStatusBucket, string> = {
  [PaStatusBucket.APPROVAL_ON_FILE]: "Approval on File",
  [PaStatusBucket.DENIAL_ON_FILE]: "Denial on File",
  [PaStatusBucket.NOT_COVERED_PLAN_EXCLUSION]:
    "Drug Not Covered / Plan Exclusion",
  [PaStatusBucket.MEDICALLY_UNNECESSARY]: "Medically Unnecessary",
  [PaStatusBucket.STEP_THERAPY_REQUIRED]: "Step Therapy Required",
  [PaStatusBucket.DONE_BY_ONSHORE]: "Done by Onshore",
  [PaStatusBucket.ASKED_CLIENT_ASSISTANCE]: "Asked Client Assistance",
  [PaStatusBucket.EXPIRED]: "Expired",
  [PaStatusBucket.PROCESSOR_ERROR]: "Processor Error",
  [PaStatusBucket.UNABLE_TO_PROCESS_CALL_REQUIRED]:
    "Unable to process through ePA - Call Required",
  [PaStatusBucket.CANCELLED_CLOSED_DUPLICATE]: "Cancelled / Closed / Duplicate",
  [PaStatusBucket.ELIGIBILITY_ERROR]: "Eligibility Error",
  [PaStatusBucket.PROGRAM_PREREQUISITE]: "Program Prerequisite",
  [PaStatusBucket.NO_REVIEW_NEEDED]: "No Review Needed",
  [PaStatusBucket.BENEFIT_LIMITATION]: "Benefit Limitation",
  [PaStatusBucket.UNSPECIFIED_SYSTEM_FAILURE]: "Unspecified System Failure",
  [PaStatusBucket.OTHERS]: "Others",
  [PaStatusBucket.NO_PREDEFINED_BUCKETS]: "(No predefined buckets)",
};

// Color categories for PA Status Bucket
export enum PaStatusBucketColorCategory {
  SUCCESS = "success", // Green - Approved/Positive outcomes
  DANGER = "danger", // Red - Denied/Rejected outcomes
  WARNING = "warning", // Orange - Requires action/Pending
  INFO = "info", // Blue - Informational
  PURPLE = "purple", // Purple - Onshore related
  NEUTRAL = "neutral", // Gray - Neutral/NA outcomes
  ERROR = "error", // Light Red - System errors
}

// Color configuration for each category
export const PaStatusBucketColors: Record<
  PaStatusBucketColorCategory,
  { textColor: string; bgColor: string }
> = {
  [PaStatusBucketColorCategory.SUCCESS]: {
    textColor: "#005D49",
    bgColor: "#E6F3F0",
  },
  [PaStatusBucketColorCategory.DANGER]: {
    textColor: "#CC0300",
    bgColor: "#FFE8E8",
  },
  [PaStatusBucketColorCategory.WARNING]: {
    textColor: "#C24400",
    bgColor: "#FFF3E6",
  },
  [PaStatusBucketColorCategory.INFO]: {
    textColor: "#0056D6",
    bgColor: "#E6F0FF",
  },
  [PaStatusBucketColorCategory.PURPLE]: {
    textColor: "#6B21A8",
    bgColor: "#F3E8FF",
  },
  [PaStatusBucketColorCategory.NEUTRAL]: {
    textColor: "#666666",
    bgColor: "#F5F5F5",
  },
  [PaStatusBucketColorCategory.ERROR]: {
    textColor: "#991B1B",
    bgColor: "#FEE2E2",
  },
};

// Mapping of PA Status Bucket to its color category
export const PaStatusBucketColorMapping: Record<
  PaStatusBucket,
  PaStatusBucketColorCategory
> = {
  // Success - Green
  [PaStatusBucket.APPROVAL_ON_FILE]: PaStatusBucketColorCategory.SUCCESS,
  [PaStatusBucket.NO_REVIEW_NEEDED]: PaStatusBucketColorCategory.SUCCESS,

  // Danger - Red (Denied outcomes)
  [PaStatusBucket.DENIAL_ON_FILE]: PaStatusBucketColorCategory.DANGER,
  [PaStatusBucket.NOT_COVERED_PLAN_EXCLUSION]:
    PaStatusBucketColorCategory.DANGER,
  [PaStatusBucket.MEDICALLY_UNNECESSARY]: PaStatusBucketColorCategory.DANGER,

  // Warning - Orange (Requires action)
  [PaStatusBucket.STEP_THERAPY_REQUIRED]: PaStatusBucketColorCategory.WARNING,
  [PaStatusBucket.ASKED_CLIENT_ASSISTANCE]: PaStatusBucketColorCategory.WARNING,
  [PaStatusBucket.BENEFIT_LIMITATION]: PaStatusBucketColorCategory.WARNING,
  [PaStatusBucket.PROGRAM_PREREQUISITE]: PaStatusBucketColorCategory.WARNING,

  // Purple - Onshore
  [PaStatusBucket.DONE_BY_ONSHORE]: PaStatusBucketColorCategory.PURPLE,

  // Error - Light Red (System errors)
  [PaStatusBucket.PROCESSOR_ERROR]: PaStatusBucketColorCategory.ERROR,
  [PaStatusBucket.ELIGIBILITY_ERROR]: PaStatusBucketColorCategory.ERROR,
  [PaStatusBucket.UNSPECIFIED_SYSTEM_FAILURE]:
    PaStatusBucketColorCategory.ERROR,
  [PaStatusBucket.UNABLE_TO_PROCESS_CALL_REQUIRED]:
    PaStatusBucketColorCategory.ERROR,

  // Neutral - Gray (NA/Closed outcomes)
  [PaStatusBucket.EXPIRED]: PaStatusBucketColorCategory.NEUTRAL,
  [PaStatusBucket.CANCELLED_CLOSED_DUPLICATE]:
    PaStatusBucketColorCategory.NEUTRAL,
  [PaStatusBucket.OTHERS]: PaStatusBucketColorCategory.NEUTRAL,
  [PaStatusBucket.NO_PREDEFINED_BUCKETS]: PaStatusBucketColorCategory.NEUTRAL,
};
